import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocket } from '../../lib/socket';
import { api } from '../../lib/api';
import { formatMoeda } from '@fiodecobre/shared';
import { useVersaoNoAr } from '../../lib/useVersaoNoAr';

// Tela de exibição no balcão (visão do cliente). Roda no celular/tablet do Atendimento — que
// ficou ocioso quando o número foi pra nuvem da Meta — num suporte virado pro cliente. Só MOSTRA
// o QR; não cobra nada. Fala com o backend pelo Socket.IO que já temos:
//  - pix:exibir_qr  → mostra o QR + valor (estado "aguardando")
//  - pix:pago       → "Pagamento aprovado" (automático, quando o sistema confere na InfinitePay)
//  - pix:limpar     → volta pro repouso
// Cores fixas da marca Cobre de propósito (é a identidade da loja, independe do tema do aparelho).
// Mockup aprovado: mockup-pix-display.html. Ver project_integracao_infinitepay_maquininha.

const COBRE = '#b06a2c';
const COBRE_ESCURO = '#8a4f1d';
const TINTA = '#2a2118';
const OK = '#15803d';

interface ExibirQr {
  cobranca_id: number;
  orcamento_id: number;
  numero: string;
  cliente_nome: string | null;
  valor: string;
  qr_data_url: string;
  link_url?: string;
}
interface Pago {
  cobranca_id: number;
  orcamento_id: number;
  numero: string | null;
  valor: string | null;
}

type Estado =
  | { fase: 'repouso' }
  | { fase: 'aguardando'; dados: ExibirQr }
  | { fase: 'pago'; numero: string | null; valor: string | null };

export function ExibicaoPix() {
  const { socket } = useSocket();
  const [estado, setEstado] = useState<Estado>({ fase: 'repouso' });
  // Confirmação leve de 1 toque do botão FINALIZAR: o 1º toque arma ("Confirmar?"), o 2º executa.
  // Local e simples de propósito — nada de confirm() nativo (bloqueia) num aparelho de balcão.
  const [confirmando, setConfirmando] = useState(false);
  const [finalizando, setFinalizando] = useState(false);
  // Kiosk auto-atualiza pós-deploy: o balcão fica aberto o dia todo e roda FORA do ErrorBoundary,
  // então um deploy podia deixá-lo apontando pra arquivos já podados (guardamos só as últimas 5
  // releases) → tela branca SEM recuperação. Recarrega sozinho ENQUANTO ainda roda, pegando os
  // arquivos atuais antes de quebrarem. A tela se restaura via /api/pix/ativa no boot.
  //
  // ⚠️ Aqui a recarga é IMEDIATA e isso é próprio deste aparelho: é um visor virado pro cliente,
  // ninguém digita nada, então não há o que se perder. O AppCel usa a mesma sonda e espera um
  // momento seguro — ver `useVersaoNoAr`, que guarda o porquê de a reação não ser compartilhada.
  //
  // O valor devolvido é exibido no rodapé pra diagnosticar se o kiosk pegou o último deploy.
  const versaoApp = useVersaoNoAr(() => window.location.reload());

  // Volta o botão pro repouso se o cliente armar sem querer e ninguém confirmar em ~4s.
  useEffect(() => {
    if (!confirmando) return;
    const t = setTimeout(() => setConfirmando(false), 4000);
    return () => clearTimeout(t);
  }, [confirmando]);

  async function finalizarAtendimento() {
    // 1º toque: arma a confirmação. 2º toque (dentro de 4s): executa.
    if (!confirmando) { setConfirmando(true); return; }
    setConfirmando(false);
    setFinalizando(true);
    try {
      // Cancela a cobrança ATIVA do terminal + limpa a tela + libera o terminal (server-side).
      // Auth branda (só `autenticar`): o balcão pode não ter permissão de caixa. Ação não destrutiva
      // pro dinheiro — Pix já recebido continua livre pra conciliação. Ver POST /api/pix/ativa/finalizar.
      await api.post('/api/pix/ativa/finalizar');
      setEstado({ fase: 'repouso' });
    } catch { /* silencioso — se falhar, o poll de /ativa eventualmente reconcilia */ }
    finally { setFinalizando(false); }
  }

  useEffect(() => {
    if (!socket) return;
    const onExibir = (d: ExibirQr) => setEstado({ fase: 'aguardando', dados: d });
    const onPago = (d: Pago) => {
      setEstado((e) => {
        // Só troca pra "pago" se for a cobrança que está na tela (ou se a tela está em repouso).
        if (e.fase === 'aguardando' && e.dados.cobranca_id !== d.cobranca_id) return e;
        return { fase: 'pago', numero: d.numero, valor: d.valor };
      });
    };
    const onLimpar = () => setEstado({ fase: 'repouso' });
    socket.on('pix:exibir_qr', onExibir);
    socket.on('pix:pago', onPago);
    socket.on('pix:limpar', onLimpar);
    return () => {
      socket.off('pix:exibir_qr', onExibir);
      socket.off('pix:pago', onPago);
      socket.off('pix:limpar', onLimpar);
    };
  }, [socket]);

  // Manifesto dedicado: enquanto a tela do balcão está aberta, ela "assume" um manifesto PWA
  // próprio (start_url /balcao/pix). Assim o "Adicionar à tela inicial" do Android instala um app
  // que abre SEMPRE o QR em tela cheia — sem cair no catálogo (que é o start_url do manifesto
  // global). Restaura o manifesto original ao sair da tela.
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) return;
    const anterior = link.getAttribute('href');
    link.setAttribute('href', '/manifest-balcao.json');
    return () => {
      if (anterior) link.setAttribute('href', anterior);
    };
  }, []);

  // Troca de fase reseta a confirmação armada (não carregar "Confirmar?" entre telas).
  useEffect(() => { setConfirmando(false); }, [estado.fase]);

  // Depois de pago, volta pro repouso sozinho (a próxima cobrança reabre).
  useEffect(() => {
    if (estado.fase !== 'pago') return;
    const t = setTimeout(() => setEstado({ fase: 'repouso' }), 12000);
    return () => clearTimeout(t);
  }, [estado.fase]);

  // Auto-recuperação: a tela busca a cobrança ATIVA ao abrir, a cada 4s, ao (re)conectar o socket e
  // sempre que o aparelho VOLTA a ficar visível. O celular do balcão apaga sozinho em ~30s; com a tela
  // apagada o navegador CONGELA os timers e DERRUBA o socket — então o "pago" chegava no vazio e só
  // aparecia quando alguém tocava o aparelho (no fechamento). Resincronizar ao acordar pega esse "pago"
  // perdido na hora. O socket é só otimização; isto sobrevive a refresh, reconexão, evento perdido e
  // tela suspensa — o que um balcão real exige. Ver GET /api/pix/ativa.
  const pagoMostradoRef = useRef<number | null>(null);
  const sincronizarAtiva = useCallback(async () => {
    try {
      const r = await api.get<{ ativa: ExibirQr & { status: string } | null }>('/api/pix/ativa');
      const a = r.ativa;
      setEstado((e) => {
        if (!a) {
          // Sem cobrança ativa: se a tela está mostrando um QR que sumiu (pago/expirado), repousa.
          return e.fase === 'aguardando' ? { fase: 'repouso' } : e;
        }
        if (a.status === 'pago') {
          if (pagoMostradoRef.current === a.cobranca_id) return e; // já mostrei o verde desta
          pagoMostradoRef.current = a.cobranca_id;
          return { fase: 'pago', numero: a.numero, valor: a.valor };
        }
        // pendente → mostra o QR (se ainda não está mostrando exatamente esta cobrança)
        if (e.fase === 'aguardando' && e.dados.cobranca_id === a.cobranca_id) return e;
        return { fase: 'aguardando', dados: a };
      });
    } catch { /* silencioso — é rede de segurança */ }
  }, []);

  useEffect(() => {
    void sincronizarAtiva();
    const t = setInterval(sincronizarAtiva, 4000);
    const onVisivel = () => { if (document.visibilityState === 'visible') void sincronizarAtiva(); };
    document.addEventListener('visibilitychange', onVisivel);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisivel); };
  }, [sincronizarAtiva]);

  // Resincroniza assim que o socket (re)conecta — pega o "pago" que tenha passado enquanto a tela
  // estava suspensa ou sem rede.
  useEffect(() => {
    if (!socket) return;
    socket.on('connect', sincronizarAtiva);
    return () => { socket.off('connect', sincronizarAtiva); };
  }, [socket, sincronizarAtiva]);

  // Mantém a tela do balcão ACESA enquanto está aberta (é um terminal dedicado num suporte). Sem isto o
  // celular apaga, congela os timers e derruba o socket no meio do pagamento — e o "Pix recebido" só
  // aparecia quando alguém tocava o aparelho. O Wake Lock se solta quando a tela some, então re-pedimos
  // a cada volta de visibilidade. Best-effort: navegador sem suporte simplesmente segue sem.
  useEffect(() => {
    const nav = navigator as Navigator & {
      wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> };
    };
    let lock: { release(): Promise<void> } | null = null;
    const pedir = async () => {
      try {
        if (nav.wakeLock && document.visibilityState === 'visible' && !lock) {
          lock = await nav.wakeLock.request('screen');
        }
      } catch { /* sem suporte / negado — segue sem */ }
    };
    void pedir();
    const onVisivel = () => {
      if (document.visibilityState === 'visible') void pedir();
      else lock = null; // tela escondeu → o navegador já soltou o lock; re-pede ao voltar
    };
    document.addEventListener('visibilitychange', onVisivel);
    return () => {
      document.removeEventListener('visibilitychange', onVisivel);
      void lock?.release().catch(() => {});
    };
  }, []);

  // Botão de escape do balcão — discreto mas alcançável, abaixo do conteúdo. Limpa a tela na hora e
  // libera o terminal sem depender de nenhuma tela do caixa aberta. O RÓTULO é contextual (o que o
  // toque faz naquele momento): aguardando o pagamento → "Cancelar Pix" (aborta a cobrança); já pago →
  // "FINALIZAR" (encerra e limpa pro próximo). Mesmo comportamento nos dois. Cores fixas da marca (não
  // tokens de tema — esta tela tem identidade própria). Armado vira amarelo "Confirmar?" por daltonismo.
  const rotuloAcao = estado.fase === 'aguardando' ? 'Cancelar Pix' : 'FINALIZAR';
  const botaoFinalizar = (
    <button
      type="button"
      onClick={finalizarAtendimento}
      disabled={finalizando}
      style={{
        marginTop: 18, padding: '11px 22px', borderRadius: 12, cursor: 'pointer',
        fontSize: 14, fontWeight: 700, letterSpacing: 0.5,
        border: confirmando ? '2px solid #b45309' : `1.5px solid ${COBRE}`,
        background: confirmando ? '#fde68a' : 'transparent',
        color: confirmando ? '#000' : COBRE_ESCURO,
        opacity: finalizando ? 0.5 : 1,
      }}
    >
      {confirmando ? 'Confirmar? Toque de novo' : rotuloAcao}
    </button>
  );

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: '#fbf7f2', color: TINTA,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        fontFamily: '-apple-system,"Segoe UI",Roboto,Arial,sans-serif',
      }}
    >
      {/* Topo da marca */}
      <div
        style={{
          padding: '28px 22px 22px', textAlign: 'center', color: '#fff',
          background: `linear-gradient(135deg, ${COBRE} 0%, ${COBRE_ESCURO} 100%)`,
        }}
      >
        <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: 1.5 }}>FIODECOBRE</div>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, opacity: 0.85, marginTop: 2 }}>
          MATERIAIS ELÉTRICOS
        </div>
      </div>

      {estado.fase === 'repouso' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#8a785f' }}>Pronto para cobrar</div>
          <div style={{ fontSize: 14, color: '#a9947a', textAlign: 'center' }}>
            Quando a vendedora iniciar o Pix, o código aparece aqui.
          </div>
        </div>
      )}

      {estado.fase === 'aguardando' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '18px 22px' }}>
          <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: COBRE_ESCURO, marginBottom: 8 }}>
            Pague com Pix
          </div>
          <div style={{ fontSize: 48, fontWeight: 800, color: TINTA, letterSpacing: -1, marginBottom: 22 }}>
            {formatMoeda(Number(estado.dados.valor))}
          </div>
          <div style={{ background: '#fff', border: '1px solid #ece2d4', borderRadius: 18, padding: 14, boxShadow: '0 6px 16px rgba(120,80,30,.10)' }}>
            <img src={estado.dados.qr_data_url} alt="QR Code Pix" style={{ display: 'block', width: 230, height: 230 }} />
          </div>
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#4a3c2b', lineHeight: 1.35 }}>
              Aponte a câmera do seu<br />celular para o código
            </div>
            <div style={{ marginTop: 10, fontSize: 13, fontWeight: 700, color: COBRE_ESCURO, background: '#f6ead9', borderRadius: 999, padding: '6px 15px', display: 'inline-block' }}>
              Confirma sozinho assim que cair
            </div>
          </div>
          {botaoFinalizar}
        </div>
      )}

      {estado.fase === 'pago' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 30, textAlign: 'center' }}>
          <div style={{ width: 130, height: 130, borderRadius: '50%', background: OK, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 28, boxShadow: '0 10px 26px rgba(21,128,61,.35)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3.2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 70, height: 70 }}>
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: 0.5, color: '#166534', lineHeight: 1.1 }}>
            PIX<br />RECEBIDO
          </div>
          {estado.valor != null && (
            <div style={{ fontSize: 38, fontWeight: 800, color: TINTA, margin: '14px 0 4px', letterSpacing: -1 }}>
              {formatMoeda(Number(estado.valor))}
            </div>
          )}
          <div style={{ fontSize: 15, color: '#6b5944', marginTop: 16 }}>Obrigado pela preferência!</div>
          {botaoFinalizar}
          <div style={{ marginTop: 22, fontSize: 12, fontWeight: 700, letterSpacing: 2, color: COBRE }}>FIODECOBRE</div>
        </div>
      )}

      {/* Rodapé com o pedido + versão que ESTE aparelho carregou (diagnóstico do kiosk) */}
      <div style={{ padding: 14, textAlign: 'center', fontSize: 12, color: '#a9947a', borderTop: '1px solid #f0e7da' }}>
        {estado.fase === 'aguardando' && `Pedido ${estado.dados.numero} · aguardando pagamento…`}
        {estado.fase === 'pago' && estado.numero && `Pedido ${estado.numero} · pago via Pix`}
        {estado.fase === 'repouso' && 'Fio de Cobre · pagamento via Pix'}
        <span style={{ display: 'block', marginTop: 4, fontSize: 10, opacity: 0.7 }}>
          {versaoApp ?? 'versão…'}
        </span>
      </div>
    </div>
  );
}
